/**
 * Processes card symbols in post content
 * Finds card symbols (♠ ♥ ♦ ♣) and the character before them,
 * then applies appropriate styling
 */
export class CardSymbolProcessor {
  constructor() {
    // Card symbols: spades, hearts, diamonds, clubs (both Unicode and emoji versions)
    // Order matters: longer emoji versions first to match them before shorter Unicode versions
    this.cardSymbols = [
      '\u2660\uFE0F', // ♠️
      '\u2665\uFE0F', // ♥️
      '\u2666\uFE0F', // ♦️
      '\u2663\uFE0F', // ♣️
      '\u2660', // ♠
      '\u2665', // ♥
      '\u2666', // ♦
      '\u2663'  // ♣
    ];
    
    // Color class mapping for card symbols
    this.cardColorClasses = {
      '\u2665': 'red',      // ♥
      '\u2665\uFE0F': 'red', // ♥️
      '\u2663': 'green',    // ♣
      '\u2663\uFE0F': 'green', // ♣️
      '\u2666': 'blue',     // ♦
      '\u2666\uFE0F': 'blue', // ♦️
      '\u2660': 'black',    // ♠
      '\u2660\uFE0F': 'black' // ♠️
    };
  }

  /**
   * Gets the color class for a card symbol
   */
  getCardColorClass(symbol) {
    return this.cardColorClasses[symbol] || 'black';
  }

  /**
   * Finds the last character in an element (including nested text nodes)
   */
  getLastCharacterFromElement(element) {
    if (!element) return null;
    
    // Get all text nodes from the element
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }
    
    if (textNodes.length === 0) return null;
    
    // Get the last text node
    const lastTextNode = textNodes[textNodes.length - 1];
    const text = lastTextNode.textContent;
    
    if (!text || text.length === 0) return null;
    
    // Get the last non-whitespace character
    for (let i = text.length - 1; i >= 0; i--) {
      if (!/\s/.test(text[i])) {
        return {
          character: text[i],
          textNode: lastTextNode,
          index: i
        };
      }
    }
    
    return null;
  }

  /**
   * Finds the character and its containing element immediately before `startNode`
   * in the DOM, walking up through inline parents if needed (e.g. <strong>♣</strong>).
   * Returns null if the immediately preceding content is whitespace or non-alphanumeric.
   *
   * @param {Node} startNode - the text node that begins with a card symbol
   * @returns {{ character: string, element: Element } | null}
   */
  _findCharBeforeNode(startNode) {
    // Walk up from startNode until we find a sibling to the left
    let node = startNode;

    while (node) {
      const parent = node.parentNode;
      if (!parent) return null;

      const siblings = Array.from(parent.childNodes);
      const idx = siblings.indexOf(node);

      for (let j = idx - 1; j >= 0; j--) {
        const prev = siblings[j];

        if (prev.nodeType === Node.TEXT_NODE) {
          // A text node directly before us — its last non-whitespace char decides
          const text = prev.textContent;
          for (let k = text.length - 1; k >= 0; k--) {
            if (/\s/.test(text[k])) return null; // space separator → stop
            if (/[\w\d]/.test(text[k])) {
              // The char is in plain text, not inside a colorable element — skip
              return null;
            }
            return null; // non-alphanumeric non-space → stop
          }
          return null;
        }

        if (prev.nodeType === Node.ELEMENT_NODE) {
          const tag = prev.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE') return null;
          if (prev.classList && prev.classList.contains('card-symbol-processed')) return null;

          const lastCharInfo = this.getLastCharacterFromElement(prev);
          if (!lastCharInfo) return null;
          if (!/[\w\d]/.test(lastCharInfo.character)) return null;

          return { character: lastCharInfo.character, element: prev };
        }
      }

      // No sibling found — if we are inside an inline element, step up and try again
      if (
        parent.nodeType === Node.ELEMENT_NODE &&
        /^(STRONG|B|EM|I|SPAN|A|S|U|SUP|SUB)$/.test(parent.tagName)
      ) {
        node = parent;
      } else {
        break;
      }
    }

    return null;
  }

  /**
   * Processes card symbols in the given content container
   */
  processCardSymbols(contentContainer) {
    if (!contentContainer) {
      console.warn('CardSymbolProcessor: Content container not found');
      return;
    }

    // Create a walker to traverse all text nodes
    const walker = document.createTreeWalker(
      contentContainer,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // Skip if node is inside script, style, or already processed elements
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') {
            return NodeFilter.FILTER_REJECT;
          }
          if (parent.classList.contains('card-symbol-processed')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    let processedCount = 0;

    textNodes.forEach((textNode) => {
      const text = textNode.textContent;
      let hasCardSymbol = false;

      // Check if text contains any card symbol
      for (const symbol of this.cardSymbols) {
        if (text.includes(symbol)) {
          hasCardSymbol = true;
          break;
        }
      }

      if (!hasCardSymbol) return;

      // Process the text node
      const parent = textNode.parentElement;
      if (!parent) return;

      // Mark parent as processed to avoid double processing
      parent.classList.add('card-symbol-processed');

      // Create a document fragment to hold the processed content
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let modified = false;

      // Process each character in the text
      for (let i = 0; i < text.length; i++) {
        let foundSymbol = null;
        let symbolLength = 0;

        // Check for card symbols (check longer emoji versions first)
        for (const symbol of this.cardSymbols) {
          if (text.substring(i, i + symbol.length) === symbol) {
            foundSymbol = symbol;
            symbolLength = symbol.length;
            break;
          }
        }

        if (foundSymbol) {
          // Find the character directly before the card symbol
          const charBeforeIndex = i - 1;
          let charBefore = null;
          let charBeforeSource = null; // 'text' or 'element'
          let charBeforeElement = null; // element if charBefore comes from an element

          // First, check if there's a character directly before in the same text node
          if (charBeforeIndex >= lastIndex && !/\s/.test(text[charBeforeIndex])) {
            charBefore = text[charBeforeIndex];
            charBeforeSource = 'text';
          } else if (i === lastIndex) {
            // Symbol is at the very start of this segment — look at the node immediately
            // before this text node in the DOM (walking up if needed when symbol is
            // the only child of an inline element like <strong>♣</strong>).
            const result = this._findCharBeforeNode(textNode);
            if (result) {
              charBefore = result.character;
              charBeforeSource = 'element';
              charBeforeElement = result.element;
            }
          }

          // Add text from lastIndex to charBeforeIndex (or to i if no charBefore)
          const textEndIndex = (charBeforeSource === 'text' && charBefore) ? charBeforeIndex : i;
          if (textEndIndex > lastIndex) {
            const beforeText = text.substring(lastIndex, textEndIndex);
            if (beforeText) {
              fragment.appendChild(document.createTextNode(beforeText));
            }
          }

          // Get color class for the card symbol
          const cardColorClass = this.getCardColorClass(foundSymbol);

          // Process character before card symbol
          if (charBefore && /[\w\d]/.test(charBefore)) {
            if (charBeforeSource === 'element' && charBeforeElement) {
              // Character comes from an element (like <strong>3</strong>)
              // We need to modify that element's last text node
              const lastCharInfo = this.getLastCharacterFromElement(charBeforeElement);
              if (lastCharInfo && lastCharInfo.textNode) {
                const lastText = lastCharInfo.textNode.textContent;
                const beforeChar = lastText.substring(0, lastCharInfo.index);
                const afterChar = lastText.substring(lastCharInfo.index + 1);
                
                // Create the colored span for the character
                const charSpan = document.createElement('span');
                charSpan.className = `card-symbol-char card-symbol-char--${cardColorClass}`;
                charSpan.textContent = charBefore;
                
                // Replace the last character in the element's text node
                const elementFragment = document.createDocumentFragment();
                if (beforeChar) {
                  elementFragment.appendChild(document.createTextNode(beforeChar));
                }
                elementFragment.appendChild(charSpan);
                if (afterChar) {
                  elementFragment.appendChild(document.createTextNode(afterChar));
                }
                
                lastCharInfo.textNode.parentNode.replaceChild(elementFragment, lastCharInfo.textNode);
                
                // Mark the element as processed to avoid double processing
                if (charBeforeElement.classList) {
                  charBeforeElement.classList.add('card-symbol-processed');
                }
                modified = true;
              }
            } else {
              // Character comes from current text node
              const charSpan = document.createElement('span');
              charSpan.className = `card-symbol-char card-symbol-char--${cardColorClass}`;
              charSpan.textContent = charBefore;
              fragment.appendChild(charSpan);
              modified = true;
            }
          } else if (charBefore && charBeforeSource === 'text') {
            // Character exists but is not alphanumeric, add it as regular text
            fragment.appendChild(document.createTextNode(charBefore));
          }

          // Create span for card symbol
          const symbolSpan = document.createElement('span');
          symbolSpan.className = `card-symbol card-symbol--${cardColorClass}`;
          symbolSpan.textContent = foundSymbol;
          fragment.appendChild(symbolSpan);
          modified = true;

          // Update lastIndex to after the card symbol
          lastIndex = i + symbolLength;
          i += symbolLength - 1; // -1 because loop will increment
        }
      }

      // Add remaining text
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
      }

      // Replace the text node with the fragment if modifications were made
      if (modified) {
        parent.replaceChild(fragment, textNode);
        processedCount++;
      } else {
        // Remove the class if no modifications were made
        parent.classList.remove('card-symbol-processed');
      }
    });

    if (processedCount > 0) {
      console.log(`Card symbol processing completed. Processed ${processedCount} text nodes.`);
    }
  }

  /**
   * Initializes card symbol processing for the current page
   */
  init() {
    // Check if we're on a post or page
    const isPostPage = document.body.classList.contains('post-template') || 
                      document.body.classList.contains('page-template') ||
                      document.querySelector('.gh-content, .js-toc-content');
    
    if (!isPostPage) {
      return;
    }

    // Find content area
    const contentContainer = document.querySelector('.gh-content, .js-toc-content');
    
    if (contentContainer) {
      // Small delay to ensure content is fully loaded
      setTimeout(() => {
        this.processCardSymbols(contentContainer);
      }, 500);
    } else {
      console.warn('CardSymbolProcessor: Content container not found');
    }
  }
}

/**
 * Initialize card symbol processing
 */
export function initCardSymbolProcessor() {
  const processor = new CardSymbolProcessor();
  processor.init();
}

